import React from "react";
import { APP_PLATFORM_LOGO_MAP, APPLE_GUIDE_STEPS_IMAGES_MAPPER } from "../../../shared/constants";
import useFancybox from "../../../hooks/useFancyBox";

const RedirectLink = ({ href, text }: { href: string; text: string }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 dark:text-blue-400 hover:underline"
    >
      {text}
    </a>
  );
};

const StepImage = ({
  step,
  className,
}: {
  step: keyof typeof APPLE_GUIDE_STEPS_IMAGES_MAPPER;
  className?: string;
}) => {
  return (
    <div className="w-max">
      <img
        src={APPLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
        alt="Apple App Store Connect"
        data-fancybox="apple-guide"
        data-src={APPLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
        className={`max-w-[900px] w-full h-auto border border-gray-300 cursor-zoom-in rounded-lg my-2 ${className}`}
      />
    </div>
  );
};

const AppleGuide: React.FC = () => {
  const [fancyboxRef] = useFancybox({});
  return (
    <section
      id="apple"
      className="scroll-mt-24 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm"
      ref={fancyboxRef}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="size-8">
        <img src={APP_PLATFORM_LOGO_MAP.ios} alt="apple logo" className="object-fit w-full h-auto"/>
        </div>
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-white">
          Apple IAP Integration
        </h2>
      </div>
      <h3 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
        Integrating App Store Connect with AppRevBooster
      </h3>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
        This guide walks you through connecting your{" "}
        <span className="font-semibold">App Store Connect</span> account to
        AppRevBooster using an{" "}
        <span className="font-semibold">App Store Connect API key</span>. This
        connection allows AppRevBooster to manage and synchronize your In-App
        Purchase(IAP) revenues directly from your AppRevBooster dashboard.
      </p>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        Follow the steps below to generate the necessary credentials and add them
        to AppRevBooster.
      </p>

      <h3 className="mt-5 font-semibold text-gray-900 dark:text-white">
        Before You Begin (Prerequisites)
      </h3>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
        Please ensure the following preliminary steps are completed:
      </p>
      <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
        <li>
          You must have <span className="font-semibold">Admin</span> or{" "}
          <span className="font-semibold">App Manager</span> role access in{" "}
          <span className="font-semibold">App Store Connect</span>.
        </li>
        <li>
          You will need access to the{" "}
          <span className="font-semibold">App Store Connect</span> platform to
          complete these steps.
        </li>
      </ol>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 1: Generate an App Store Connect API Key
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          To allow AppRevBooster to access your App Store products, you must
          create an API key in App Store Connect.
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            Sign in to{" "}
            <RedirectLink
              href="https://appstoreconnect.apple.com/"
              text="App Store Connect"
            />{" "}
            and navigate to:
            {" "}
            <RedirectLink
              href="https://appstoreconnect.apple.com/access/integrations/api"
              text="Users and Access → Integrations → Team Keys"
            />
            .
            <br />
            Click <span className="font-semibold">+</span> to create a new API
            key.
            <StepImage step="1" />
          </li>
          <li>
            In the <span className="font-semibold">Generate API Key</span>{" "}
            window:
            <ul className="mt-1 pl-4 list-none space-y-1">
              <li>
                ○​ Enter a recognizable name for your key.
              </li>
              <li>
                ○​ Set the access level to{" "}
                <span className="font-semibold">Developer, Finance</span>{" "}
                (required for AppRevBooster).
                <StepImage step="2" />
              </li>
            </ul>
          </li>
          <li>
            After the key is created, click{" "}
            <span className="font-semibold">Download</span> to save the{" "}
            <span className="font-semibold">.p8</span> private key file.
            <div className="mt-2 rounded-md bg-yellow-50 dark:bg-yellow-900/30 p-3 border border-yellow-200 dark:border-yellow-800 max-w-[900px]">
              <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium flex items-center gap-1">
                ⚠️ Important:
              </p>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                You can download this key <span className="font-semibold">only once</span>. Store it safely.
              </p>
            </div>
            <StepImage step="3" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 2: Add the API Key to AppRevBooster
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          In <span className="font-semibold">AppRevBooster</span>, go to:{" "}
          <RedirectLink
            href="/integrations"
            text="Integrations → Revenue Sources → Add Account → Apple IAP"
          />
          .
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            Fill in the required fields as described below:
          </li>
        </ol>

        <div className="mt-2 pl-6">
          <h5 className="font-semibold text-gray-900 dark:text-white">
            API Key
          </h5>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Upload the <span className="font-semibold">.p8</span> API key file
            you downloaded from App Store Connect.
            <StepImage step="4" />
          </p>
        </div>

        <div className="mt-3 pl-6">
          <h5 className="font-semibold text-gray-900 dark:text-white">
            Issuer ID
          </h5>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Copy the <span className="font-semibold">Issuer ID</span> from:
            {" "}
            <RedirectLink
              href="https://appstoreconnect.apple.com/access/integrations/api"
              text="App Store Connect → Users and Access → Integrations → Team Keys"
            />
            .
            <br />
            The Issuer ID is displayed{" "}
            <span className="font-semibold">above</span> the API Keys table.
            <StepImage step="5" />
          </p>
        </div>

        <div className="mt-3 pl-6">
          <h5 className="font-semibold text-gray-900 dark:text-white">
            Key ID
          </h5>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            In the same section{" "}
            <RedirectLink
              href="https://appstoreconnect.apple.com/access/integrations/api"
              text="(Team Keys)"
            />
            , locate your newly created key in the list.
            <br />
            Copy the <span className="font-semibold">Key ID</span> shown next to
            it.
            <StepImage step="6" />
          </p>
        </div>
      </div>

      <div className="mt-3 pl-6">
        <h5 className="font-semibold text-gray-900 dark:text-white">
          Vendor ID
        </h5>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          To find your Apple Vendor ID:
        </p>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            In{" "}
            <RedirectLink
              href="https://appstoreconnect.apple.com/"
              text="App Store Connect"
            />
            , return to the main dashboard.
          </li>
          <li>
            Navigate to{" "}
            <RedirectLink
              href="https://appstoreconnect.apple.com/trends"
              text="Sales and Trends"
            />
            .
          </li>
          <li>
            Your Vendor ID (sometimes referred to as{" "}
            <span className="font-semibold">Organization ID</span>) will be
            displayed on this page. Copy and save it.
            <br />
            <StepImage step="7" />
          </li>
        </ol>
      </div>

      <div className="mt-5">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Step 3: Complete Integration in AppRevBooster
        </h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          You now have all the necessary details to finish the Apple IAP
          integration in AppRevBooster:
        </p>
        <ul className="mt-2 list-disc pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>
            The <span className="font-semibold">App Store Connect API Key (.p8)</span>{" "}
            file (from Step 1).
          </li>
          <li>
            The <span className="font-semibold">Issuer ID</span> (from Step 2).
          </li>
          <li>
            The <span className="font-semibold">Key ID</span> (from Step 2).
          </li>
          <li>
            The <span className="font-semibold">Vendor ID</span> (from Step 3).
          </li>
        </ul>
        <ol className="mt-2 list-decimal pl-6 text-sm text-gray-700 dark:text-gray-200 space-y-1">
          <li>Log in to your AppRevBooster account.</li>
          <li>
            Navigate to the integration or Add Account section for Apple IAP.
          </li>
          <li>
            Upload the <span className="font-semibold">.p8 API Key file</span>.
          </li>
          <li>
            Paste the copied <span className="font-semibold">Issuer ID</span>,{" "}
            <span className="font-semibold">Key ID</span>, and{" "}
            <span className="font-semibold">Vendor ID</span> into their
            corresponding fields.
          </li>
          <li>Save your changes to complete the integration.</li>
        </ol>
        <StepImage step="8" className="!w-auto h-[500px] mt-4" />
        <div className="mt-3 rounded-md bg-green-50 dark:bg-green-900/30 p-3 border border-green-200 dark:border-green-800 max-w-[900px]">
          <p className="text-sm text-green-800 dark:text-green-300 font-medium">
            Integration Complete!
          </p>
          <p className="mt-1 text-sm text-green-700 dark:text-green-400">
            Your Apple App Store Connect account is now integrated with
            AppRevBooster. You can begin managing and analyzing your in-app
            purchase and subscription data seamlessly from your dashboard.
          </p>
        </div>
      </div>

    </section>
  );
};

export default AppleGuide;
